import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchSection } from '../../../components/common/SearchSection';
import '../../../components/common/SearchSection.css';
import { LeaseUnitOption } from './types';
import { leasesApi } from './services/leaseApi';

interface LeaseUnitSectionProps {
  selectedUnit: LeaseUnitOption | null;
  onUnitSelect: (unit: LeaseUnitOption | null) => void;
  includeLeaseId?: number;
  error?: string;
}

const LeaseUnitSection: React.FC<LeaseUnitSectionProps> = ({
  selectedUnit,
  onUnitSelect,
  includeLeaseId,
  error
}) => {
  const [units, setUnits] = useState<LeaseUnitOption[]>([]);

  useEffect(() => {
    leasesApi.getUnitOptions(includeLeaseId)
      .then(setUnits)
      .catch((err) => console.error('Error loading lease units:', err));
  }, [includeLeaseId]);

  const searchFunction = useCallback(async (searchTerm: string) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return units.slice(0, 10);
    }

    return units.filter((unit) => {
      const haystack = [
        unit.propertyName,
        unit.propertyDescription,
        unit.unitAddressLine,
        unit.categoryName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    }).slice(0, 10);
  }, [units]);

  const getByIdFunction = useCallback(async (id: number) => {
    const unit = units.find((u) => u.propertiesAddressesId === id);
    if (unit) {
      return unit;
    }
    const options = await leasesApi.getUnitOptions(includeLeaseId);
    const found = options.find((u) => u.propertiesAddressesId === id);
    if (!found) {
      throw new Error(`Unit ${id} not found`);
    }
    return found;
  }, [units, includeLeaseId]);

  const renderResultItem = useCallback((unit: LeaseUnitOption, onAdd: () => void, isDisabled?: boolean) => (
    <div className="search-result-item">
      <div className="search-result-item-content">
        <div>{unit.propertyName}</div>
        {unit.propertyDescription && <div>{unit.propertyDescription}</div>}
        <div>{unit.unitAddressLine}</div>
        {unit.categoryName && <div>{unit.categoryName}</div>}
      </div>
      <div className="search-result-item-actions">
        <button type="button" className="app-btn app-btn-primary btn-small" onClick={onAdd} disabled={isDisabled}>
          Add
        </button>
      </div>
    </div>
  ), []);

  const renderSelectedItem = useCallback((unit: LeaseUnitOption, onRemove: () => void) => (
    <div className="search-selected-item">
      <div className="search-selected-item-content">
        <div>{unit.propertyName}</div>
        <div>{unit.unitAddressLine}</div>
      </div>
      <div className="search-selected-item-actions">
        <button type="button" className="app-btn app-btn-secondary btn-small" onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  ), []);

  const selectedItem = useMemo(() => selectedUnit, [selectedUnit]);

  return (
    <section className="form-section">
      <h3>Unit</h3>
      <div className="form-section-content">
        <SearchSection<LeaseUnitOption>
          selectionMode="single"
          selectedItem={selectedItem}
          onItemSelect={onUnitSelect}
          searchFunction={searchFunction}
          getByIdFunction={getByIdFunction}
          renderResultItem={renderResultItem}
          renderSelectedItem={renderSelectedItem}
          getItemId={(unit) => unit.propertiesAddressesId}
          label="Search Units"
          placeholder="Search by property or address..."
          entityType="lease-unit"
          emptyStateMessage="No available units found"
          selectedLabel="Selected Unit"
        />
        {error && <div className="app-error-message">{error}</div>}
      </div>
    </section>
  );
};

export default LeaseUnitSection;
